// Memories — videogram-style rendering of trip media (images, videos)
// curated from sealed deals + Twin event memory. POC scope: pull
// images from /v1/aide/sessions history + Alexandria sealed-deal
// cards. Real impl will project trip-grouped clips via the videogram
// substrate (read-only reference at /mnt/Storage-1/worktrees/videogram).

import { useEffect, useState } from "react";
import { AIDE_HTTP_BASE, AIDE_HTTP_PATH } from "../lib/env";


interface TripCard {
  id: string;
  title: string;
  subtitle: string;
  date: string;
  imageURL: string;
  videoURL?: string;
  city?: string;
}


// POC stub data — a couple of past + upcoming trips with hero images.
// Future: pull from sealed deals + ReceiptLedger + Twin geo events.
const SAMPLE_TRIPS: TripCard[] = [
  {
    id: "trip_kyoto_2026_03",
    title: "Kyoto · Spring Weekend",
    subtitle: "Aoyama Stay · Latour anniversary dinner · onsen mornings",
    date: "Mar 2026",
    imageURL: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=1200",
    videoURL: "",
    city: "Kyoto",
  },
  {
    id: "trip_hakone_2025_11",
    title: "Hakone · Family Onsen",
    subtitle: "Yumoto Resort · kayak lake morning · open-air museum",
    date: "Nov 2025",
    imageURL: "https://images.unsplash.com/photo-1545569310-a8e2c44ad29c?w=1200",
    videoURL: "",
    city: "Hakone",
  },
  {
    id: "trip_ginza_2025_09",
    title: "Ginza Sushi Night",
    subtitle: "Sushi Tama · agent-to-agent bid · seven friends",
    date: "Sep 2025",
    imageURL: "https://images.unsplash.com/photo-1579584425555-c3ce17fd4351?w=1200",
    city: "Tokyo",
  },
  {
    id: "trip_cdg_pickup_2026_05",
    title: "CdG SS26 Pickup",
    subtitle: "Aoyama flagship · trench + structured shirt · 11:00 slot",
    date: "May 2026",
    imageURL: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=1200",
    city: "Tokyo",
  },
  {
    id: "trip_minato_2025_05",
    title: "L'Ambassade · Anniversary",
    subtitle: "Duck confit · Latour 2005 by the glass · cheese trolley",
    date: "May 2025",
    imageURL: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200",
    city: "Tokyo",
  },
  {
    id: "trip_okinawa_2025_07",
    title: "Okinawa · Summer",
    subtitle: "Reef snorkeling · 5-day resort · sealed via PACT",
    date: "Jul 2025",
    imageURL: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200",
    city: "Okinawa",
  },
];


export function MemoriesSurface() {
  const [trips, setTrips] = useState<TripCard[]>(SAMPLE_TRIPS);
  const [loading, setLoading] = useState(false);
  // Future: GET /v1/aide/memories?consumer_id=X to pull real trip
  // groupings from sealed deals. For POC, stub data above.
  useEffect(() => {
    void AIDE_HTTP_BASE; void AIDE_HTTP_PATH; void setLoading; void setTrips;
  }, []);
  void loading;
  return (
    <div className="px-4 pt-3 pb-6">
      <header className="mb-4 flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-medium text-(--color-ink)">Memories</h1>
          <p className="mt-1 text-xs text-(--color-ink-faint)">
            Your trips and stays — photos, video moments, sealed-deal scrapbook.
          </p>
        </div>
        <span className="text-[10px] text-(--color-ink-faint)">
          {trips.length} trip{trips.length === 1 ? "" : "s"}
        </span>
      </header>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {trips.map((trip) => (
          <TripTile key={trip.id} trip={trip} />
        ))}
      </div>
    </div>
  );
}


function TripTile({ trip }: { trip: TripCard }) {
  return (
    <article
      className="group overflow-hidden rounded-(--radius-card) bg-(--color-canvas-card) ring-1 ring-white/5"
      role="button"
      tabIndex={0}
    >
      <div className="relative aspect-square bg-stone-900">
        <img
          src={trip.imageURL}
          alt={trip.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
        />
        {trip.videoURL && (
          <div className="absolute right-2 top-2 rounded-(--radius-pill) bg-black/60 px-2 py-0.5 text-[10px] text-white">
            ▶ video
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-2.5">
          <div className="flex items-baseline justify-between text-[10px] uppercase tracking-wider text-white/70">
            <span>{trip.city ?? ""}</span>
            <span>{trip.date}</span>
          </div>
          <h2 className="mt-0.5 text-sm font-medium leading-tight text-white">{trip.title}</h2>
        </div>
      </div>
      <div className="px-2.5 py-2 text-[11px] text-(--color-ink-muted) line-clamp-2">
        {trip.subtitle}
      </div>
    </article>
  );
}
